import { Show, type JSX } from 'solid-js'
import type { ViewProps } from 'wdc-cube-solid'
import type { SharedHistoryScope } from 'mormaii-web-presentation/shared-history'

import { BucketDialog } from '../../components/bucket-readings'
import { Brand } from '../../components/icons'
import { historyCell } from '../../components/history-cells'
import { PatientAnalytics } from '../../components/patient-analytics'
import { DataTable, Pager } from '../../components/table'
import { Loading, Panel } from '../../components/ui'

import Css from './shared-history.module.scss'

/**
 * O historico que um paciente compartilhou por link.
 *
 * Tela PUBLICA: nao esta dentro do `hub`, nao exige sessao e por isso desenha a
 * propria casca - nao ha menu nem cabecalho de secao em volta dela. Quem chega aqui
 * clicou num link, e pode nunca ter tido conta.
 *
 * O painel e a tabela sao os MESMOS do detalhe do colaborador. Muda so de onde os
 * numeros vieram.
 */
export function SharedHistoryView(props: ViewProps<SharedHistoryScope>): JSX.Element {
    return (
        <div class={Css.page}>
            <header class={Css.top}>
                <div class={Css.brand}>
                    <Brand class={Css.brandIcon} />
                    <span class={Css.brandName}>
                        Mormaii <span class={Css.brandAccent}>Mais Saúde</span>
                    </span>
                </div>
            </header>

            <main class={Css.body}>
                <Show
                    when={!props.scope.invalidLink}
                    fallback={<InvalidLink title={props.scope.invalidTitle} text={props.scope.invalidText} />}
                >
                    <Show when={!props.scope.loading || props.scope.analytics} fallback={<Loading />}>
                        <div class={Css.screen}>
                            <Panel>
                                <div class={Css.summary}>
                                    <Summary label="Paciente" value={props.scope.patientName} />
                                    <Summary label="Última atualização" value={props.scope.lastUpdate} />
                                    <Summary label="Link válido até" value={props.scope.expiresAt} />
                                </div>
                            </Panel>

                            {/*
                                As duas visoes sao do SCOPE (`view`), e trocar de uma
                                para a outra passa pelo presenter - e o que faz a
                                escolha sobreviver ao F5 e viajar no link.
                            */}
                            <div class={Css.tabs}>
                                <Tab
                                    current={props.scope.view === 'chart'}
                                    onSelect={() => void props.scope.onShowChart()}
                                >
                                    Gráficos
                                </Tab>
                                <Tab
                                    current={props.scope.view === 'list'}
                                    onSelect={() => void props.scope.onShowList()}
                                >
                                    Medições
                                </Tab>
                            </div>

                            <Show
                                when={props.scope.view === 'chart'}
                                fallback={
                                    <Panel title="Medições do período" flush>
                                        <DataTable
                                            columns={props.scope.historyColumns.map((column) => ({
                                                field: column.field,
                                                label: column.label,
                                                sortable: true,
                                                sorted: column.sorted,
                                                descending: column.descending,
                                                onSort: column.onSort,
                                                movable: column.movable,
                                                onDrop: (source) => void column.onDrop(source)
                                            }))}
                                            rows={props.scope.historyRows}
                                            layout={props.scope.historyLayout}
                                            loading={props.scope.loading}
                                            cell={historyCell}
                                            emptyText="Nenhuma medição no período"
                                        />

                                        <Show when={props.scope.bucket}>
                                            {(bucket) => <BucketDialog scope={bucket()} />}
                                        </Show>

                                        <Pager
                                            page={props.scope.historyPage}
                                            pageCount={props.scope.historyPageCount}
                                            pages={props.scope.historyPageWindow}
                                            onPrevious={() => void props.scope.onPreviousPage()}
                                            onNext={() => void props.scope.onNextPage()}
                                            onGoTo={(page) => void props.scope.onGoToPage(page)}
                                        />
                                    </Panel>
                                }
                            >
                                <Show when={props.scope.analytics}>
                                    {(analytics) => <PatientAnalytics scope={analytics()} />}
                                </Show>
                            </Show>
                        </div>
                    </Show>
                </Show>
            </main>
        </div>
    )
}

function Summary(props: { label: string; value: string }): JSX.Element {
    return (
        <div class={Css.fact}>
            <span class={Css.factLabel}>{props.label}</span>
            <span class={Css.factValue}>{props.value}</span>
        </div>
    )
}

function Tab(props: { current: boolean; onSelect: () => void; children: JSX.Element }): JSX.Element {
    return (
        <button
            type="button"
            aria-current={props.current ? 'true' : undefined}
            onClick={props.onSelect}
            class={Css.tab}
            classList={{ [Css.current]: props.current }}
        >
            {props.children}
        </button>
    )
}

type InvalidLinkProps = { title: string; text: string }

/**
 * O link nao trouxe codigo, ou venceu.
 *
 * Sem codigo acontece com link truncado, com endereco digitado a mao - que a aplicacao
 * recusa desde que so aceita o que ela mesma produziu - e com bookmark antigo. Sem este
 * aviso a tela ficava EM BRANCO. O vencido caia num dialogo de erro inesperado. Os
 * textos vem do scope, porque os dois casos dizem coisas diferentes.
 */
function InvalidLink(props: InvalidLinkProps): JSX.Element {
    return (
        <div class={Css.noCode}>
            <span class={Css.noCodeIcon}>
                <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.75"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                >
                    <path d="M12 9v4M12 17h.01M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" />
                </svg>
            </span>
            <p class={Css.noCodeTitle}>{props.title}</p>
            <p class={Css.noCodeText}>{props.text}</p>
        </div>
    )
}
